// src/components/layout/MdiTabBar.jsx
import React                         from "react"
import { useSelector, useDispatch }  from "react-redux"
import {
  selectTabs,
  selectActiveTabId,
  setActiveTab,
  closeTab,
}                                    from "../../features/tabsSlice"
import { removeForm }                from "../../features/formsSlice"

const MAX_TABS = 8

// Module-code → dot color (matches sidebar + dashboard palette)
const TAB_COLORS = {
  dashboard:        "#185FA5",
  customer:         "#1D9E75",
  supplier:         "#1D9E75",
  salesorder:       "#EF9F27",
  purchorder:       "#EF9F27",
  invoice:          "#C0392B",
  journal:          "#534AB7",
  receipt:          "#185FA5",
  payment:          "#185FA5",
  trialbal:         "#1D9E75",
  freight:          "#EF9F27",
  sendforapproval:  "#EF9F27",
  itemmaster:       "#16A085",
  employee:         "#8E44AD",
}

const MdiTabBar = () => {
  const dispatch    = useDispatch()
  const tabs        = useSelector(selectTabs)
  const activeTabId = useSelector(selectActiveTabId)

  // ── Activate a tab ───────────────────────────────────────
  const handleActivate = (id) => {
    if (id !== activeTabId) dispatch(setActiveTab(id))
  }

  // ── Close a tab — with "next active" logic ───────────────
  const handleClose = (id, e) => {
    if (e) e.stopPropagation()

    // Optional: warn on dirty form
    const tab = tabs.find(t => t.id === id)
    if (tab?.isDirty) {
      if (!window.confirm(`"${tab.label}" has unsaved changes. Close anyway?`)) {
        return
      }
    }

    // If closing the currently-active tab, pick a neighbour to activate
    if (id === activeTabId) {
      const idx       = tabs.findIndex(t => t.id === id)
      const remaining = tabs.filter(t => t.id !== id)
      if (remaining.length > 0) {
        const nextTab = remaining[Math.min(idx, remaining.length - 1)]
        dispatch(setActiveTab(nextTab.id))
      } else {
        dispatch(setActiveTab(null))            // back to dashboard
      }
    }

    dispatch(removeForm(id))
    dispatch(closeTab(id))
  }

  // ── Middle-click also closes (common UX) ─────────────────
  const handleMouseDown = (id, e) => {
    if (e.button === 1) {
      e.preventDefault()
      handleClose(id, e)
    }
  }

  return (
    <div style={styles.bar}>
      {tabs.map((tab) => (
        <Tab
          key={tab.id}
          tab={tab}
          isActive={tab.id === activeTabId}
          onActivate={() => handleActivate(tab.id)}
          onClose={(e) => handleClose(tab.id, e)}
          onMouseDown={(e) => handleMouseDown(tab.id, e)}
        />
      ))}

      {/* Tab count indicator */}
      {tabs.length > 0 && (
        <div
          style={{
            ...styles.tabWarn,
            color: tabs.length >= MAX_TABS ? "#C0392B"
                 : tabs.length >= MAX_TABS - 1 ? "#EF9F27"
                 : "#888",
          }}
          title={
            tabs.length >= MAX_TABS
              ? "Maximum tabs reached — close one to open another"
              : `${tabs.length} of ${MAX_TABS} tabs open`
          }
        >
          {tabs.length}/{MAX_TABS}
        </div>
      )}
    </div>
  )
}

// ── Single tab ─────────────────────────────────────────────
const Tab = ({ tab, isActive, onActivate, onClose, onMouseDown }) => {
  const dotColor = TAB_COLORS[tab.formType] || "#888"

  return (
    <div
      style={{ ...styles.tab, ...(isActive ? styles.tabActive : {}) }}
      onClick={onActivate}
      onMouseDown={onMouseDown}
      title={tab.label}
    >
      <div style={{ ...styles.dot, background: dotColor }} />

      <span style={styles.label}>{tab.label}</span>

      {tab.isDirty && (
        <div style={styles.dirtyDot} title="Unsaved changes" />
      )}

      <button
        style={styles.closeBtn}
        onClick={onClose}
        onMouseDown={(e) => e.stopPropagation()}
        title="Close tab"
        aria-label={`Close ${tab.label}`}
      >
        ✕
      </button>
    </div>
  )
}

// ── Styles (unchanged — kept inline so no CSS file change needed) ──
const styles = {
  bar: {
    height: 33,
    background: "var(--color-background-secondary, #f1f3f5)",
    borderBottom: "0.5px solid var(--color-border-secondary, #e4e7eb)",
    display: "flex",
    alignItems: "flex-end",
    padding: "0 6px",
    gap: 1,
    flexShrink: 0,
    overflowX: "auto",
    overflowY: "hidden",
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "0 9px",
    height: 27,
    border: "0.5px solid transparent",
    borderBottom: "none",
    borderRadius: "4px 4px 0 0",
    cursor: "pointer",
    background: "transparent",
    fontSize: 11.5,
    color: "var(--color-text-secondary, #52606d)",
    whiteSpace: "nowrap",
    flexShrink: 0,
    transition: "all .12s",
    userSelect: "none",
    maxWidth: 220,
  },
  tabActive: {
    background: "var(--color-background-primary, #ffffff)",
    color: "var(--color-text-primary, #1f2933)",
    fontWeight: 600,
    borderColor: "var(--color-border-secondary, #e4e7eb)",
    boxShadow: "0 -2px 0 #185FA5 inset",
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    flexShrink: 0,
  },
  label: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 160,
  },
  dirtyDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#EF9F27",
    marginLeft: 2,
  },
  closeBtn: {
    width: 16,
    height: 16,
    border: "none",
    background: "transparent",
    color: "inherit",
    opacity: 0.5,
    cursor: "pointer",
    fontSize: 11,
    lineHeight: 1,
    padding: 0,
    marginLeft: 2,
    borderRadius: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tabWarn: {
    marginLeft: "auto",
    fontSize: 10.5,
    fontWeight: 600,
    padding: "0 8px",
    alignSelf: "center",
  },
}

export default MdiTabBar